import { ThrowLiteError } from '../lite-error.js';

let states = null;
function getPreviousDeformableShadowBounds(generator, mesh, kind) {
  const providers = states?.get(generator)?.providers;
  let bounds = null;
  for (let i = providers?.length ?? 0, kindIndex = kind === "morph" ? 1 : 0; --i > kindIndex; ) {
    const provider = providers[i];
    if (provider?.applies(mesh)) {
      bounds = provider.getLocalBounds(mesh, bounds);
    }
  }
  return bounds;
}
function updateShadowMesh(entry) {
  const bounds = entry.provider.getLocalBounds(entry.source);
  const min = bounds?.[0] ?? entry.source.boundMin;
  const max = bounds?.[1] ?? entry.source.boundMax;
  const previous = entry._bounds;
  let changed = false;
  for (let i = 0; i < 3; i++) {
    changed = !Object.is(previous[i], min?.[i]) || !Object.is(previous[i + 3], max?.[i]) || changed;
    previous[i] = min?.[i];
    previous[i + 3] = max?.[i];
  }
  entry.shadow.boundMin = min;
  entry.shadow.boundMax = max;
  return changed;
}
function createShadowMesh(source, provider) {
  const shadow = Object.create(source);
  const entry = { source, shadow, provider, _bounds: [], _version: 0 };
  Object.defineProperty(shadow, "worldMatrixVersion", {
    configurable: true,
    get: () => source.worldMatrixVersion + entry._version
  });
  updateShadowMesh(entry);
  return entry;
}
function mapCasterMeshes(state, casterMeshes) {
  if (state.sourceMeshes === casterMeshes) {
    return state.shadowMeshes;
  }
  const entries = [];
  const shadowMeshes = casterMeshes.map((mesh) => {
    const provider = state.providers.find((candidate) => candidate?.applies(mesh));
    if (!provider) {
      return mesh;
    }
    const entry = createShadowMesh(mesh, provider);
    entries.push(entry);
    return entry.shadow;
  });
  state.sourceMeshes = casterMeshes;
  state.shadowMeshes = shadowMeshes;
  state.entries = entries;
  return shadowMeshes;
}
function prepareExistingState(generator, state) {
  const existing = generator._shadowTaskState;
  if (existing && existing._casterMeshes === state.sourceMeshes && state.shadowMeshes) {
    existing._casterMeshes = state.shadowMeshes;
  }
}
function restoreSourceCasters(taskState, casterMeshes) {
  taskState._casterMeshes = casterMeshes;
}
function enableDeformableShadowBounds(generator, provider) {
  const kindIndex = provider.kind === "morph" ? 1 : 0;
  let state = states?.get(generator);
  if (state) {
    if (!state.providers[kindIndex]) {
      state.providers[kindIndex] = provider;
      state.sourceMeshes = void 0;
    }
    return;
  }
  const preload = generator._preloadShadowTask;
  const ensure = generator._ensureShadowTaskState;
  const render = generator._renderShadowMap;
  if (!preload || !ensure || !render) {
    ThrowLiteError(462);
  }
  state = {
    providers: [],
    preload,
    ensure,
    render
  };
  state.providers[kindIndex] = provider;
  (states ??= /* @__PURE__ */ new WeakMap()).set(generator, state);
  const previousReplacer = generator._replaceShadowTaskHooks;
  generator._replaceShadowTaskHooks = previousReplacer ?? ((nextEnsure, nextRender) => {
    state.ensure = nextEnsure;
    state.render = nextRender;
  });
  generator._preloadShadowTask = (casterMeshes) => preload(mapCasterMeshes(state, casterMeshes));
  generator._ensureShadowTaskState = (engine, scene, casterMeshes) => {
    prepareExistingState(generator, state);
    const shadowMeshes = mapCasterMeshes(state, casterMeshes);
    const taskState = state.ensure(engine, scene, shadowMeshes);
    restoreSourceCasters(taskState, casterMeshes);
    return taskState;
  };
  generator._renderShadowMap = (engine, taskState) => {
    const shadowMeshes = state.shadowMeshes;
    const sourceMeshes = state.sourceMeshes;
    if (!shadowMeshes || !sourceMeshes) {
      return state.render(engine, taskState);
    }
    for (const entry of state.entries ?? []) {
      if (updateShadowMesh(entry)) {
        entry._version++;
      }
    }
    taskState._casterMeshes = shadowMeshes;
    try {
      return state.render(engine, taskState);
    } finally {
      restoreSourceCasters(taskState, sourceMeshes);
    }
  };
}

export { enableDeformableShadowBounds, getPreviousDeformableShadowBounds };
//# sourceMappingURL=deformable-shadow-casters.js.map
